import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import get from 'lodash.get';
import { PAGE_SIZE } from '~/modules/common/const';
import { PROJECT_DAL } from '~/modules/common/enums';
import { ProjectWorkflowStage } from '~/types';

export const GET_PROJECTS_QUERY = gql`
  query GetPageOfProjectsFilteredByDataAccessLevels(
    $page: Int!
    $pageSize: Int!
    $searchTerm: String
    $dataAccessLevels: [String]
  ) {
    getPageOfProjectsFilteredByDataAccessLevels(
      page: $page
      pageSize: $pageSize
      searchTerm: $searchTerm
      dataAccessLevels: $dataAccessLevels
    ) {
      id
      displayText
    }
  }
`;

export const PAGE_OF_PROJECTS_AVAILABLE_FOR_FILTERING_RESOURCE_USERS_QUERY = gql`
  query PageOfProjectsAvailableForFilteringResourceUsersSearch(
    $page: Int!
    $pagesize: Int!
    $filter: PageOfProjectsAvailableForFilteringResourceUsersFilter
  ) {
    pageOfProjectsAvailableForFilteringResourceUsers(
      page: $page
      pagesize: $pagesize
      filter: $filter
    ) {
      id
      displayText
    }
  }
`;

const NON_ARCHIVED_PROJECT_WORKFLOW_STAGES = [
  ProjectWorkflowStage.Initiate,
  ProjectWorkflowStage.Planning,
  ProjectWorkflowStage.Execution,
  ProjectWorkflowStage.Closeout
];

const useGetPageOfProjectsFilteredByDataAccessLevelsSearch = ({
  dataAccessLevels,
  searchTerm,
  skip
}) => {
  const { data, loading } = useQuery(GET_PROJECTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      page: 1,
      pageSize: PAGE_SIZE + 1,
      searchTerm,
      dataAccessLevels
    },
    context: {
      debounceKey: 'project-search-option',
      debounceTimeout: 250
    },
    skip
  });

  return {
    projects: get(data, 'getPageOfProjectsFilteredByDataAccessLevels', []),
    loading
  };
};

const usePageOfProjectsAvailableForFilteringResourceUsersSearch = ({
  searchTerm,
  skip
}) => {
  const { data, loading } = useQuery(
    PAGE_OF_PROJECTS_AVAILABLE_FOR_FILTERING_RESOURCE_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        page: 1,
        pagesize: PAGE_SIZE + 1,
        filter: {
          projectWorkflowStages: NON_ARCHIVED_PROJECT_WORKFLOW_STAGES,
          textSearch: searchTerm
        }
      },
      context: {
        debounceKey: 'project-search-option',
        debounceTimeout: 250
      },
      skip
    }
  );

  return {
    projects: get(data, 'pageOfProjectsAvailableForFilteringResourceUsers', []),
    loading
  };
};

export const useSearchableProjects = ({
  dataAccessLevels = [
    PROJECT_DAL.PROJECT_MANAGER,
    PROJECT_DAL.PROGRAM_MANAGER,
    PROJECT_DAL.CLIENT_MANAGER,
    PROJECT_DAL.PROJECT_ADMIN
  ],
  searchTerm,
  usePageOfProjectsAvailableForFilteringResourceUsersQuery = false
}) => {
  const projectsFilteredByDataAccessLevels = useGetPageOfProjectsFilteredByDataAccessLevelsSearch(
    {
      dataAccessLevels,
      searchTerm,
      skip: usePageOfProjectsAvailableForFilteringResourceUsersQuery
    }
  );

  const pageOfProjectsAvailableForFilteringResourceUsers = usePageOfProjectsAvailableForFilteringResourceUsersSearch(
    {
      searchTerm,
      skip: !usePageOfProjectsAvailableForFilteringResourceUsersQuery
    }
  );

  const {
    projects,
    loading
  } = usePageOfProjectsAvailableForFilteringResourceUsersQuery
    ? pageOfProjectsAvailableForFilteringResourceUsers
    : projectsFilteredByDataAccessLevels;

  const projectsToShowInList = (projects || [])
    .slice(0, PAGE_SIZE)
    .map(project => ({
      ...project,
      value: project.id,
      label: project.displayText,
      key: project.displayText
    }));

  return {
    projects: projectsToShowInList,
    loading,
    hasMore: projects.length > PAGE_SIZE
  };
};
